import {
  Alert,
  Button,
  Card,
  Drawer,
  Input,
  Modal,
  TextArea,
} from '@heroui/react';
import { memo, useEffect, useMemo, useRef, useState } from 'react';
import type { FormEvent, KeyboardEvent } from 'react';
import type { ReactNode } from 'react';
import type { PublicAiCartAction, PublicAiChatProduct, PublicAiChatResponse, PublicAiEscalationSubmitResponse, PublicProduct, PublicTenant } from '../../../shared/types';
import type { CartLine } from '../../contexts/CartContext';
import { publicAiMessageParagraphs, sanitizePublicAiMessage } from '../../../shared/public-ai-message';
import { productRequiresVisualConfiguration } from '../../../shared/product-configurability';
import { apiRequest, jsonBody } from '../../lib/api';
import { formatLocal, formatUsd } from '../../lib/currency';
import { useToast } from '../../contexts/ToastContext';
import { usePublicMenuTheme } from '../PublicMenuThemeProvider';
import pidoLogo from '../../../assets/logo-pido.png';

const QUICK_PROMPTS = [
  '¿Qué me recomiendas?',
  'Quiero algo con pollo.',
  'Tengo $10.',
  'Somos 3 personas.',
  '¿Qué promociones tienen?',
] as const;

const CHAT_HISTORY_LIMIT = 24;
const SESSION_ID_PATTERN = /^[a-f0-9-]{36}$/i;
const ASSISTANT_NAME = 'Pido';

type EscalationForm = {
  customerName: string;
  customerPhone: string;
  complaint: string;
};

export type ChatMessageDeliveryStatus = 'sending' | 'sent' | 'failed';

export type ChatMessage = {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  deliveryStatus?: ChatMessageDeliveryStatus;
  products?: PublicAiChatProduct[];
  actionApplied?: boolean;
  noResults?: boolean;
};

interface AIOrderChatProps {
  branchId: string;
  cartItemCount: number;
  cartLines: CartLine[];
  products: ReadonlyMap<string, PublicProduct>;
  tenant: PublicTenant;
  onApplyCartAction(action: PublicAiCartAction): boolean;
  onOpenProduct(product: PublicProduct): void;
  onQuickAddProduct(product: PublicProduct): boolean;
  renderInlineProductConfigurator?(product: PublicProduct, onAdded: (product: PublicProduct) => void, onCancel: () => void): ReactNode;
  onOpenCart(): void;
  assistantAvailable?: boolean;
}

export function createPendingUserMessage(id: string, text: string): ChatMessage {
  return { id, role: 'user', text, deliveryStatus: 'sending' };
}

export function updateUserMessageDelivery(
  messages: ChatMessage[],
  id: string,
  deliveryStatus: ChatMessageDeliveryStatus,
): ChatMessage[] {
  return messages.map((message) => (
    message.id === id && message.role === 'user'
      ? { ...message, deliveryStatus }
      : message
  ));
}

export function updateExpandedProductByMessage(
  current: Record<string, string | null>,
  messageId: string,
  productId: string | null,
): Record<string, string | null> {
  return { ...current, [messageId]: productId };
}

export function AssistantAvatar({ compact = false }: { compact?: boolean }) {
  return (
    <div
      aria-label={`Avatar de ${ASSISTANT_NAME}, asistente para pedir`}
      className={`${compact ? 'size-8 p-0.5' : 'size-11 p-1'} grid shrink-0 place-items-center rounded-full bg-white/90 shadow-sm ring-2 ring-[var(--menu-chat-surface)]`}
      role="img"
    >
      <img alt="" aria-hidden="true" className="h-full w-full object-contain" src={pidoLogo} />
    </div>
  );
}

export function AssistantIdentity({ showStatus }: { showStatus: boolean }) {
  return (
    <div className="flex min-w-0 items-center gap-2.5">
      <AssistantAvatar compact />
      <div className="min-w-0">
        <Drawer.Heading className="menu-text-primary truncate text-base font-black tracking-tight">{ASSISTANT_NAME}</Drawer.Heading>
        {showStatus ? (
          <p aria-label="Estado del asistente: Disponible" className="mt-0.5 inline-flex items-center gap-1.5 text-xs font-bold text-[var(--menu-chat-online)]">
            <span aria-hidden="true" className="size-2 rounded-full bg-[var(--menu-chat-online)]" />
            Disponible
          </p>
        ) : null}
      </div>
    </div>
  );
}

export function openExistingCart(closeChat: (open: boolean) => void, onOpenCart: () => void) {
  closeChat(false);
  onOpenCart();
}

export function shouldSubmitFromKey(event: Pick<KeyboardEvent<HTMLTextAreaElement>, 'key' | 'shiftKey'>): boolean {
  return event.key === 'Enter' && !event.shiftKey;
}

export function ChatMessageText({ text }: { text: string }) {
  const paragraphs = publicAiMessageParagraphs(text);
  if (paragraphs.length === 0) return null;
  return (
    <div className="space-y-3 whitespace-pre-wrap break-words text-left">
      {paragraphs.map((paragraph, index) => <p key={`${index}:${paragraph}`}>{paragraph}</p>)}
    </div>
  );
}

export function AIOrderChatHeader({
  assistantAvailable,
  cartItemCount,
  onOpenCart,
  onCloseChat,
  onRequestHelp,
}: {
  assistantAvailable: boolean;
  cartItemCount: number;
  onOpenCart(): void;
  onCloseChat(open: boolean): void;
  onRequestHelp(): void;
}) {
  return (
    <div className="flex min-w-0 items-center justify-between gap-3">
      <AssistantIdentity showStatus={assistantAvailable} />
      <div className="flex shrink-0 items-center gap-2">
        <Button aria-label="Pedir ayuda humana" className="rounded-full border border-[var(--menu-border)] bg-[var(--menu-chat-surface)] text-[var(--menu-text-primary)]" isIconOnly size="sm" variant="secondary" onPress={onRequestHelp}>
          <span aria-hidden="true">⚠</span>
        </Button>
        <Button
          aria-label="Abrir carrito"
          className="relative rounded-full"
          isIconOnly
          size="sm"
          variant="secondary"
          onPress={() => openExistingCart(onCloseChat, onOpenCart)}
        >
          <span aria-hidden="true">🛒</span>
          {cartItemCount > 0 ? (
            <span aria-label={`${cartItemCount} artículos en el carrito`} className="absolute -right-1 -top-1 grid min-w-5 place-items-center rounded-full bg-[var(--menu-accent)] px-1 text-[10px] font-black leading-5 text-[var(--menu-accent-foreground)]">
              {cartItemCount}
            </span>
          ) : null}
        </Button>
      </div>
    </div>
  );
}

export function AIOrderChatComposer({
  draft,
  pending,
  onDraftChange,
  onSubmit,
  onKeyDown,
}: {
  draft: string;
  pending: boolean;
  onDraftChange(value: string): void;
  onSubmit(): void;
  onKeyDown(event: KeyboardEvent<HTMLTextAreaElement>): void;
}) {
  return (
    <div className="flex w-full items-end gap-2">
      <TextArea
        aria-label="Escribe tu mensaje para el asistente"
        className="min-w-0 flex-1 rounded-[var(--menu-radius)] border border-[var(--menu-border)] bg-[var(--menu-chat-assistant-bubble)] text-[var(--menu-text-primary)] placeholder:text-[var(--menu-text-muted)] shadow-none focus-visible:border-[var(--menu-primary)] focus-visible:ring-2 focus-visible:ring-[var(--menu-primary)]"
        placeholder="Escribe qué te gustaría pedir..."
        rows={1}
        value={draft}
        variant="secondary"
        onChange={(event) => onDraftChange(event.target.value)}
        onKeyDown={onKeyDown}
      />
      <Button
        aria-label="Enviar mensaje"
        className="h-11 w-11 shrink-0 rounded-full border border-[var(--menu-primary)] bg-[var(--menu-primary)] text-[var(--menu-primary-foreground)] shadow-surface ring-1 ring-[var(--menu-border)] transition-colors hover:bg-[var(--menu-primary-hover)] focus-visible:ring-2 focus-visible:ring-[var(--menu-primary)]"
        isDisabled={pending || !draft.trim()}
        isIconOnly
        size="lg"
        onPress={onSubmit}
      >
        <span aria-hidden="true">➤</span>
      </Button>
    </div>
  );
}

export function TypingIndicator() {
  return (
    <div className="flex items-start gap-2">
      <AssistantAvatar compact />
      <div aria-label="El asistente está procesando tu mensaje" className="flex items-center gap-1.5 rounded-[var(--menu-radius)] border border-border bg-[var(--menu-chat-assistant-bubble)] px-4 py-3.5" role="status">
        <span aria-hidden="true" className="h-2 w-2 animate-bounce rounded-full bg-[var(--menu-text-muted)]" style={{ animationDelay: '0ms' }} />
        <span aria-hidden="true" className="h-2 w-2 animate-bounce rounded-full bg-[var(--menu-text-muted)]" style={{ animationDelay: '150ms' }} />
        <span aria-hidden="true" className="h-2 w-2 animate-bounce rounded-full bg-[var(--menu-text-muted)]" style={{ animationDelay: '300ms' }} />
      </div>
    </div>
  );
}

function useDesktopDrawer(): boolean {
  const [desktop, setDesktop] = useState(() => (
    typeof window !== 'undefined' && window.matchMedia('(min-width: 768px)').matches
  ));

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const media = window.matchMedia('(min-width: 768px)');
    const update = () => setDesktop(media.matches);
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);

  return desktop;
}

function needsConfiguration(product: PublicProduct, branchId: string): boolean {
  return productRequiresVisualConfiguration(product, branchId);
}

function productDisplayPrice(product: PublicProduct, branchId: string): { cents: number; from: boolean } {
  if (product.priceMode !== 'variante') return { cents: product.effectivePriceUsdCents, from: false };
  const prices = product.variants
    .filter((variant) => !branchId || !variant.unavailableBranchIds.includes(branchId))
    .map((variant) => {
      if (product.promoType === 'percentage') return Math.max(0, Math.round(variant.priceUsdCents * (1 - product.promoValue / 100)));
      if (product.promoType === 'fixed') return Math.max(0, Math.round(product.promoValue));
      return variant.priceUsdCents;
    });
  return { cents: prices.length ? Math.min(...prices) : 0, from: true };
}

function readSessionId(key: string): string {
  const stored = sessionStorage.getItem(key);
  if (stored && SESSION_ID_PATTERN.test(stored)) return stored;
  const next = crypto.randomUUID();
  sessionStorage.setItem(key, next);
  return next;
}

export function AIOrderChatRecommendationCard({
  item,
  messageId,
  product,
  branchId,
  tenant,
  onOpenProduct,
  onQuickAddProduct,
  expanded,
  completed,
  localNotice,
  onConfigureProduct,
  renderInlineProductConfigurator,
  onProductAdded,
  onCancelConfigure,
}: {
  item: PublicAiChatProduct;
  messageId: string;
  product?: PublicProduct;
  branchId: string;
  tenant: PublicTenant;
  onOpenProduct(product: PublicProduct): void;
  onQuickAddProduct(product: PublicProduct): boolean;
  expanded?: boolean;
  completed?: boolean;
  localNotice?: string;
  onConfigureProduct?(product: PublicProduct): void;
  renderInlineProductConfigurator?: AIOrderChatProps['renderInlineProductConfigurator'];
  onProductAdded?(product: PublicProduct): void;
  onCancelConfigure?(): void;
}) {
  const imageUrl = product?.images[0] ?? item.imageUrl;
  const displayPrice = product ? productDisplayPrice(product, branchId) : { cents: item.effectivePriceUsdCents, from: false };
  const requiresConfiguration = product ? needsConfiguration(product, branchId) : item.requiresConfiguration;
  const localPrice = formatLocal(displayPrice.cents, tenant.exchangeRate, tenant.localCurrency, tenant.currencyDecimals, tenant.currencySymbol);
  return (
    <Card key={`${messageId}:${item.id}`} className="menu-product-card menu-panel w-full min-w-0 border sm:max-w-none" variant="default">
      <Card.Content className="grid min-w-0 grid-cols-[72px_minmax(0,1fr)] gap-3 p-3">
        {imageUrl ? (
          <img alt={item.name} className="h-[72px] w-[72px] rounded-[var(--menu-radius)] object-cover" src={imageUrl} />
        ) : (
          <div aria-label="Producto sin imagen" className="menu-panel-alt grid h-[72px] w-[72px] place-items-center rounded-[var(--menu-radius)] text-2xl" role="img">○</div>
        )}
        <div className="min-w-0 space-y-2">
          <div>
            <h4 className="menu-text-primary truncate text-sm font-black">{product?.name ?? item.name}</h4>
            <p className="menu-text-secondary line-clamp-2 text-xs leading-5">{product?.description || item.description || 'Consulta disponible en el menú actual.'}</p>
          </div>
          <div className="flex flex-col gap-0.5 text-xs">
            <strong className="menu-price text-sm font-black">{displayPrice.from ? `Desde ${localPrice}` : localPrice}</strong>
            <span className="menu-text-muted">{formatUsd(displayPrice.cents)}</span>
          </div>
          {product?.promoType !== 'none' ? <span className="menu-promo inline-flex w-fit rounded-full px-2 py-0.5 text-[10px] font-black">PROMO</span> : null}
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="secondary" onPress={() => product && onOpenProduct(product)}>
              Ver
            </Button>
            <Button
              className="menu-critical-cta"
              isDisabled={!product || completed}
              size="sm"
              onPress={() => {
                if (!product) return;
                if (requiresConfiguration) {
                  onConfigureProduct?.(product);
                  return;
                }
                if (onQuickAddProduct(product)) onProductAdded?.(product);
              }}
            >
              {completed ? '✓ Agregado al carrito' : (requiresConfiguration ? 'Configurar' : 'Agregar al carrito')}
            </Button>
          </div>
          {localNotice ? <p className="text-xs font-semibold text-[var(--menu-chat-online)]">{localNotice}</p> : null}
        </div>
      </Card.Content>
      {expanded && product && renderInlineProductConfigurator ? (
        <Card.Footer className="border-t border-border p-0">
          <div className="min-w-0 flex-1 px-0 pb-0 pt-3">
            {renderInlineProductConfigurator(product, (added) => onProductAdded?.(added), () => onCancelConfigure?.())}
          </div>
        </Card.Footer>
      ) : null}
    </Card>
  );
}

export const AIOrderChat = memo(function AIOrderChat({
  branchId,
  cartItemCount,
  cartLines,
  products,
  tenant,
  onApplyCartAction,
  onOpenProduct,
  onQuickAddProduct,
  renderInlineProductConfigurator,
  onOpenCart,
  assistantAvailable = false,
}: AIOrderChatProps) {
  const desktop = useDesktopDrawer();
  const { show } = useToast();
  const { theme, portalContainer } = usePublicMenuTheme();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const [pending, setPending] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [retryText, setRetryText] = useState<string | null>(null);
  const [retryMessageId, setRetryMessageId] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState('');
  const [hasUnread, setHasUnread] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [helpSubmitting, setHelpSubmitting] = useState(false);
  const [helpError, setHelpError] = useState<string | null>(null);
  const [helpForm, setHelpForm] = useState<EscalationForm>({ customerName: '', customerPhone: '', complaint: '' });
  const [expandedProductByMessage, setExpandedProductByMessage] = useState<Record<string, string | null>>({});
  const [completedCards, setCompletedCards] = useState<Set<string>>(() => new Set());
  const [localNotices, setLocalNotices] = useState<Record<string, string>>({});
  const messagesRef = useRef<HTMLDivElement | null>(null);
  const stickToBottomRef = useRef(true);
  const panelClassName = theme.appearance.chatStyle === 'surfaceAlt' ? 'menu-panel-alt' : 'menu-panel';
  const sessionStorageKey = useMemo(
    () => `menucloud.public-ai-chat.session:${tenant.id}:${branchId || 'no-branch'}`,
    [branchId, tenant.id],
  );
  const cartPayload = useMemo(() => ({
    lines: cartLines.map((line) => ({
      lineId: line.id,
      productId: line.product.id,
      variantId: line.configuration.variantId ?? null,
      quantity: line.quantity,
      configuration: line.configuration,
      name: line.product.name,
      unitPriceUsdCents: line.unitPriceUsdCents,
    })),
  }), [cartLines]);
  const visibleMessages = messages.slice(-CHAT_HISTORY_LIMIT);

  useEffect(() => {
    try {
      setSessionId(readSessionId(sessionStorageKey));
    } catch {
      setSessionId(crypto.randomUUID());
    }
    setMessages([]);
    setDraft('');
    setRetryText(null);
    setRetryMessageId(null);
    setErrorMessage(null);
    setHelpError(null);
    setHasUnread(false);
  }, [sessionStorageKey]);

  useEffect(() => {
    const container = messagesRef.current;
    if (!container) return;
    if (stickToBottomRef.current) {
      container.scrollTop = container.scrollHeight;
      setHasUnread(false);
      return;
    }
    setHasUnread(true);
  }, [visibleMessages, pending]);

  const handleScroll = () => {
    const container = messagesRef.current;
    if (!container) return;
    const distance = container.scrollHeight - container.scrollTop - container.clientHeight;
    stickToBottomRef.current = distance < 48;
    if (stickToBottomRef.current) setHasUnread(false);
  };

  const scrollToLatest = () => {
    const container = messagesRef.current;
    if (!container) return;
    container.scrollTop = container.scrollHeight;
    stickToBottomRef.current = true;
    setHasUnread(false);
  };

  const sendMessage = async (rawMessage: string, options?: { retryMessageId?: string }) => {
    if (pending) return;
    const message = rawMessage.trim();
    if (!message) return;
    if (!branchId) {
      show('Selecciona una sucursal para usar el asistente.', 'error');
      return;
    }
    if (!sessionId || !SESSION_ID_PATTERN.test(sessionId)) {
      show('No se pudo iniciar la sesión del asistente. Intenta nuevamente.', 'error');
      return;
    }

    setOpen(true);
    setPending(true);
    setErrorMessage(null);
    setRetryText(null);
    setRetryMessageId(null);
    const userMessageId = options?.retryMessageId ?? crypto.randomUUID();
    if (options?.retryMessageId) {
      setMessages((current) => updateUserMessageDelivery(current, userMessageId, 'sending'));
    } else {
      setMessages((current) => [...current, createPendingUserMessage(userMessageId, message)]);
      setDraft('');
    }

    try {
      const response = await apiRequest<PublicAiChatResponse>('/api/public/ai/chat', {
        method: 'POST',
        auth: false,
        body: jsonBody({
          message,
          sessionId,
          branchId,
          cart: cartPayload,
        }),
      });
      const applied = response.cartAction ? onApplyCartAction(response.cartAction) : false;
      const assistantProducts = (response.products ?? []).filter((item) => {
        const current = products.get(item.id);
        return !current || !branchId || !current.unavailableBranchIds.includes(branchId);
      });
      setMessages((current) => [...updateUserMessageDelivery(current, userMessageId, 'sent'), {
        id: crypto.randomUUID(),
        role: 'assistant',
        text: sanitizePublicAiMessage(response.message),
        products: response.products ? assistantProducts : undefined,
        actionApplied: response.cartAction ? applied : undefined,
        noResults: Boolean(response.products && assistantProducts.length === 0),
      }]);
    } catch (error) {
      const messageText = error instanceof Error ? error.message : 'El asistente no está disponible en este momento.';
      setMessages((current) => updateUserMessageDelivery(current, userMessageId, 'failed'));
      setRetryText(message);
      setRetryMessageId(userMessageId);
      setErrorMessage(messageText);
    } finally {
      setPending(false);
    }
  };

  const handleSubmit = async () => {
    await sendMessage(draft);
  };

  const handleRetry = async () => {
    if (!retryText || !retryMessageId) return;
    await sendMessage(retryText, { retryMessageId });
  };

  const updateHelpForm = (field: keyof EscalationForm, value: string) => {
    setHelpForm((current) => ({ ...current, [field]: value }));
    setHelpError(null);
  };

  const submitHelpRequest = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!branchId) {
      setHelpError('Selecciona una sucursal para pedir ayuda.');
      return;
    }
    if (!sessionId || !SESSION_ID_PATTERN.test(sessionId)) {
      setHelpError('No se pudo iniciar la sesión del asistente. Intenta nuevamente.');
      return;
    }

    setHelpSubmitting(true);
    setHelpError(null);
    try {
      await apiRequest<PublicAiEscalationSubmitResponse>('/api/public/ai/escalations', {
        method: 'POST',
        auth: false,
        body: jsonBody({
          sessionId,
          branchId,
          customerName: helpForm.customerName,
          customerPhone: helpForm.customerPhone,
          complaint: helpForm.complaint,
        }),
      });
      show('Tu solicitud fue enviada a recepción.', 'success');
      setHelpForm({ customerName: '', customerPhone: '', complaint: '' });
      setHelpOpen(false);
    } catch (error) {
      setHelpError(error instanceof Error ? error.message : 'No se pudo enviar la solicitud.');
    } finally {
      setHelpSubmitting(false);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (!shouldSubmitFromKey(event)) return;
    event.preventDefault();
    void handleSubmit();
  };

  const openConfigurator = (product: PublicProduct) => {
    setOpen(false);
    onOpenProduct(product);
  };

  const markProductAdded = (messageId: string, product: PublicProduct) => {
    const cardId = `${messageId}:${product.id}`;
    setCompletedCards((current) => new Set(current).add(cardId));
    setLocalNotices((current) => ({ ...current, [cardId]: `Agregaste ${product.name} al carrito.` }));
    setExpandedProductByMessage((current) => ({ ...current, [messageId]: null }));
  };

  return (
    <>
      <div className={`fixed right-4 z-30 pb-[env(safe-area-inset-bottom)] md:right-6 md:pb-0 ${cartItemCount > 0 ? 'bottom-24 md:bottom-6' : 'bottom-4 md:bottom-6'}`}>
        <Button
          aria-label="Abrir asistente de pedidos"
          className="relative h-auto min-h-14 rounded-full border border-[var(--menu-primary)] bg-[var(--menu-primary)] px-3 py-3 text-sm font-black text-[var(--menu-primary-foreground)] shadow-surface ring-1 ring-[var(--menu-border)] transition-colors hover:bg-[var(--menu-primary-hover)] focus-visible:ring-2 focus-visible:ring-[var(--menu-primary)] sm:px-5"
          size="lg"
          onPress={() => setOpen(true)}
        >
          <span className="relative mr-2 grid size-8 place-items-center rounded-full bg-white/90 p-0.5 ring-1 ring-[var(--menu-border)]" aria-hidden="true">
            <img alt="" className="h-full w-full object-contain" src={pidoLogo} />
            {assistantAvailable ? <span className="absolute -right-0.5 -top-0.5 size-2.5 rounded-full bg-[var(--menu-chat-online)] ring-2 ring-[var(--menu-chat-surface)]" /> : null}
          </span>
          <span className="hidden sm:inline">Pedir con {ASSISTANT_NAME}</span>
          <span className="sm:hidden">{ASSISTANT_NAME}</span>
          {hasUnread ? <span aria-label="Nuevo mensaje" className="absolute -right-1 -top-1 size-3 rounded-full bg-[var(--menu-promo)] ring-2 ring-[var(--menu-surface)]" /> : null}
        </Button>
      </div>

      <Drawer.Backdrop
        UNSTABLE_portalContainer={portalContainer ?? undefined}
        isOpen={open}
        variant="blur"
        onOpenChange={setOpen}
      >
        <Drawer.Content className={desktop ? undefined : 'bg-[var(--menu-chat-composer)]'} placement={desktop ? 'right' : 'bottom'}>
          <Drawer.Dialog className={`${desktop ? 'h-dvh w-[min(94vw,460px)]' : 'h-dvh w-full'} ${panelClassName} flex max-h-dvh flex-col overflow-hidden bg-[var(--menu-chat-background)] p-0 sm:pt-5`}>
            {!desktop && <Drawer.Handle />}
            {/* <Drawer.CloseTrigger aria-label="Cerrar asistente" /> */}
            <Drawer.Header className="border-b border-border bg-[var(--menu-chat-surface)] px-4 py-2.5 sm:px-5">
              <AIOrderChatHeader
                assistantAvailable={assistantAvailable}
                cartItemCount={cartItemCount}
                onCloseChat={setOpen}
                onOpenCart={onOpenCart}
                onRequestHelp={() => setHelpOpen(true)}
              />
            </Drawer.Header>

            <Drawer.Body className="relative flex min-h-0 flex-1 flex-col gap-2 bg-[var(--menu-chat-background)] px-0 py-0">
              <div ref={messagesRef} className="min-h-0 flex-1 space-y-3 overflow-y-auto px-3 py-2 sm:px-5" onScroll={handleScroll}>
                {visibleMessages.length === 0 && !pending ? (
                  <div className="space-y-3 py-1">
                    <div className="menu-panel-alt rounded-[var(--menu-radius)] border border-dashed border-border px-3 py-2 text-sm">
                      <p className="menu-text-primary font-semibold">Pregúntame por productos, promociones o ayuda para armar tu pedido.</p>
                    </div>
                    <Button className="w-full rounded-[var(--menu-radius)] border border-[var(--menu-border)] bg-[var(--menu-chat-surface)] text-[var(--menu-text-primary)]" variant="secondary" onPress={() => setHelpOpen(true)}>
                      Necesito ayuda de una persona
                    </Button>
                    <div className="flex flex-wrap gap-2">
                      {QUICK_PROMPTS.map((prompt) => (
                        <Button key={prompt} className="rounded-full border border-[var(--menu-border)] bg-[var(--menu-chat-surface)] text-[var(--menu-text-primary)]" variant="secondary" onPress={() => void sendMessage(prompt)}>
                          {prompt}
                        </Button>
                      ))}
                    </div>
                  </div>
                ) : null}

                {visibleMessages.map((message) => (
                  <div key={message.id} className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`min-w-0 space-y-3 ${message.role === 'user' ? 'max-w-[92%] items-end text-right' : 'w-full max-w-none sm:max-w-[92%]'}`}>
                      <div className={`flex items-start gap-2 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                        {message.role === 'assistant' ? <AssistantAvatar compact /> : null}
                        <div>
                          {message.role === 'assistant' ? <p className="menu-text-muted mb-1 text-xs font-bold">{ASSISTANT_NAME}</p> : null}
                          <div className={`rounded-[var(--menu-radius)] px-4 py-3 text-sm shadow-sm ${message.role === 'user' ? 'bg-[var(--menu-chat-user-bubble)] text-[var(--menu-chat-user-foreground)]' : 'border border-border bg-[var(--menu-chat-assistant-bubble)] text-[var(--menu-chat-assistant-foreground)]'}`}>
                            <ChatMessageText text={message.text} />
                          </div>
                        </div>
                      </div>

                      {message.products && message.products.length > 0 ? (
                        <div className="grid gap-3">
                          {message.products.map((item) => {
                            const product = products.get(item.id);
                            return (
                              <AIOrderChatRecommendationCard
                                key={`${message.id}:${item.id}`}
                                branchId={branchId}
                                item={item}
                                messageId={message.id}
                                product={product}
                                tenant={tenant}
                                onOpenProduct={openConfigurator}
                                onQuickAddProduct={onQuickAddProduct}
                                expanded={expandedProductByMessage[message.id] === item.id}
                                completed={completedCards.has(`${message.id}:${item.id}`)}
                                localNotice={localNotices[`${message.id}:${item.id}`]}
                                onConfigureProduct={(target) => setExpandedProductByMessage((current) => updateExpandedProductByMessage(current, message.id, target.id))}
                                renderInlineProductConfigurator={renderInlineProductConfigurator}
                                onProductAdded={(target) => markProductAdded(message.id, target)}
                                onCancelConfigure={() => setExpandedProductByMessage((current) => updateExpandedProductByMessage(current, message.id, null))}
                              />
                            );
                          })}
                        </div>
                      ) : null}

                      {message.noResults ? (
                        <div className="menu-panel-alt rounded-[var(--menu-radius)] border border-dashed border-border px-4 py-3 text-xs">
                          <p className="menu-text-secondary">No encontré productos disponibles con esa consulta en esta sucursal.</p>
                        </div>
                      ) : null}

                      {message.actionApplied ? (
                        <div className="inline-flex rounded-full bg-[var(--menu-chat-online)] px-3 py-1 text-xs font-semibold text-[var(--menu-chat-online-foreground)]">
                          Cambio aplicado al carrito
                        </div>
                      ) : null}
                    </div>
                  </div>
                ))}

                {pending ? <TypingIndicator /> : null}
              </div>

              {hasUnread ? (
                <div className="pointer-events-none absolute inset-x-0 bottom-2 z-10 flex justify-center px-3">
                  <Button className="pointer-events-auto rounded-full border border-[var(--menu-border)] bg-[var(--menu-chat-surface)] px-4 text-[var(--menu-text-primary)] shadow-surface" size="sm" variant="secondary" onPress={scrollToLatest}>
                    Ver mensaje nuevo
                  </Button>
                </div>
              ) : null}

              {errorMessage ? (
                <div className="px-4 sm:px-5">
                  <Alert status="danger">
                    <Alert.Indicator />
                    <Alert.Content>
                      <Alert.Title>No pude responder ahora</Alert.Title>
                      <Alert.Description>{errorMessage}</Alert.Description>
                    </Alert.Content>
                    <div className="ml-auto">
                      <Button size="sm" variant="secondary" onPress={() => void handleRetry()}>
                        Reintentar
                      </Button>
                    </div>
                  </Alert>
                </div>
              ) : null}
            </Drawer.Body>

            <Drawer.Footer className="relative border-t border-border bg-[var(--menu-chat-composer)] px-3 py-2 pb-[calc(env(safe-area-inset-bottom)+1rem)] shadow-[0_-12px_28px_rgba(15,23,42,0.08)] after:pointer-events-none after:absolute after:inset-x-0 after:top-full after:h-16 after:bg-[var(--menu-chat-composer)] after:content-[''] sm:px-4 sm:pb-2">
              <AIOrderChatComposer
                draft={draft}
                pending={pending}
                onDraftChange={setDraft}
                onKeyDown={handleKeyDown}
                onSubmit={() => void handleSubmit()}
              />
            </Drawer.Footer>
          </Drawer.Dialog>
        </Drawer.Content>
      </Drawer.Backdrop>

      <Modal.Backdrop
        UNSTABLE_portalContainer={portalContainer ?? undefined}
        isOpen={helpOpen}
        onOpenChange={setHelpOpen}
        variant="blur"
      >
        <Modal.Container placement="center" scroll="inside">
          <Modal.Dialog className={`${panelClassName} w-[calc(100vw-2rem)] max-w-md`}>
            <Modal.CloseTrigger aria-label="Cerrar solicitud de ayuda" />
            <form onSubmit={submitHelpRequest}>
              <Modal.Header className="border-b border-border px-5 py-4">
                <Modal.Heading className="menu-text-primary text-lg font-black">Pedir ayuda humana</Modal.Heading>
                <p className="menu-text-secondary mt-1 text-sm">Deja tus datos y una queja o problema para que recepción pueda contactarte.</p>
              </Modal.Header>
              <Modal.Body className="grid gap-4 px-5 py-4">
                <label className="grid gap-1.5 text-sm font-bold">
                  <span>Nombre</span>
                  <Input
                    aria-label="Nombre"
                    maxLength={100}
                    minLength={2}
                    name="customerName"
                    placeholder="Tu nombre"
                    required
                    value={helpForm.customerName}
                    variant="secondary"
                    onChange={(event) => updateHelpForm('customerName', event.target.value)}
                  />
                </label>
                <label className="grid gap-1.5 text-sm font-bold">
                  <span>Teléfono</span>
                  <Input
                    aria-label="Teléfono"
                    maxLength={40}
                    minLength={5}
                    name="customerPhone"
                    placeholder="Número para contactarte"
                    required
                    type="tel"
                    value={helpForm.customerPhone}
                    variant="secondary"
                    onChange={(event) => updateHelpForm('customerPhone', event.target.value)}
                  />
                </label>
                <label className="grid gap-1.5 text-sm font-bold">
                  <span>Queja o problema</span>
                  <TextArea
                    aria-label="Queja o problema"
                    maxLength={1000}
                    minLength={10}
                    name="complaint"
                    placeholder="Cuéntanos qué ocurrió"
                    required
                    rows={5}
                    value={helpForm.complaint}
                    variant="secondary"
                    onChange={(event) => updateHelpForm('complaint', event.target.value)}
                  />
                </label>
                {helpError ? (
                  <Alert status="danger">
                    <Alert.Indicator />
                    <Alert.Content>
                      <Alert.Title>No se pudo enviar</Alert.Title>
                      <Alert.Description>{helpError}</Alert.Description>
                    </Alert.Content>
                  </Alert>
                ) : null}
              </Modal.Body>
              <Modal.Footer className="flex flex-col-reverse gap-2 border-t border-border px-5 py-4 sm:flex-row sm:justify-end">
                <Button isDisabled={helpSubmitting} type="button" variant="secondary" onPress={() => setHelpOpen(false)}>
                  Cancelar
                </Button>
                <Button className="menu-critical-cta" isPending={helpSubmitting} type="submit">
                  Enviar solicitud
                </Button>
              </Modal.Footer>
            </form>
          </Modal.Dialog>
        </Modal.Container>
      </Modal.Backdrop>
    </>
  );
});
